import { useState, useMemo } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { formatDayMonth } from '../../../utils/overviewDates';
import type { UpcomingDeparture } from '../../../utils/panorama/panoramaToday';
import { VacationAvatar } from '../../../components/VacationDpTable/components/VacationAvatar';
import { EmptyChart } from './DashboardCard';

const PAGE_SIZE = 6;

interface UpcomingDeparturesListProps {
	departures: UpcomingDeparture[];
}

function DepartureRow({ item }: { item: UpcomingDeparture }) {
	return (
		<li className="flex flex-col gap-2 py-2.5">
			<div className="flex items-center gap-3">
				<VacationAvatar name={item.name} photoUrl={item.photoUrl} />
				<div className="min-w-0 flex-1">
					<p className="truncate text-sm font-medium text-raro-gray-900">{item.name}</p>
				</div>
				<span className="shrink-0 rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-700">
					em {item.daysToStart}d
				</span>
			</div>
			<div className="flex flex-col gap-1.5">
				{item.returnsOn && (
					<span className="inline-block w-fit rounded-lg bg-teal-100 px-2 py-1 text-xs font-semibold text-teal-700">
						De férias · volta {formatDayMonth(item.returnsOn)}
					</span>
				)}
				<p className="text-xs text-raro-gray-500">
					{formatDayMonth(item.startDate)} a {formatDayMonth(item.endDate)} · {item.days} dias
					{item.clientNames.length > 0 && ` · ${item.clientNames[0]}`}
				</p>
			</div>
		</li>
	);
}

export function UpcomingDeparturesList({ departures }: UpcomingDeparturesListProps) {
	const [page, setPage] = useState(1);

	const totalPages = Math.ceil(departures.length / PAGE_SIZE);
	const currentPage = Math.min(Math.max(1, page), totalPages);

	const paginatedDepartures = useMemo(
		() => departures.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
		[departures, currentPage]
	);

	if (currentPage !== page) {
		setPage(currentPage);
	}

	if (departures.length === 0) {
		return <EmptyChart message="Nenhuma saída prevista nos próximos 30 dias." height={160} />;
	}

	return (
		<div className="flex h-full flex-col">
			<div className="flex-1">
				<ul className="divide-y divide-raro-gray-100">
					{paginatedDepartures.map(item => (
						<DepartureRow key={item.collaboratorId} item={item} />
					))}
				</ul>
			</div>

			{totalPages > 1 && (
				<div className="mt-auto border-t border-raro-gray-100 pt-3 flex items-center justify-between">
					<button
						onClick={() => setPage(p => Math.max(1, p - 1))}
						disabled={currentPage === 1}
						className="flex h-6 w-6 items-center justify-center rounded text-raro-gray-400 disabled:opacity-30 hover:bg-raro-gray-100"
						aria-label="Página anterior"
					>
						<ChevronLeftIcon className="h-4 w-4" />
					</button>

					<p className="text-xs text-raro-gray-500">
						{(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, departures.length)} de {departures.length}
					</p>

					<button
						onClick={() => setPage(p => Math.min(totalPages, p + 1))}
						disabled={currentPage === totalPages}
						className="flex h-6 w-6 items-center justify-center rounded text-raro-gray-400 disabled:opacity-30 hover:bg-raro-gray-100"
						aria-label="Próxima página"
					>
						<ChevronRightIcon className="h-4 w-4" />
					</button>
				</div>
			)}
		</div>
	);
}
